import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Modal,
  Card,
  Row,
  Col,
  Container,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";

import NsdlPanCardService from "../../../services/NsdlPanCardService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";

const PANApplication = () => {
  const { applicationType } = useParams();

  const [loading, setLoading] = useState(false);
  const [commercialData, setCommercialData] = useState(null);

  const [appMode, setAppMode] = useState("E-KYC");
  const [appType, setAppType] = useState(applicationType || "");
  const [category, setCategory] = useState("P");
  const [gender, setGender] = useState("");
  const [panType, setPanType] = useState("Physical PAN Card / e-PAN Card");
  const [checkbox, setCheckbox] = useState(false);

  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");

  const [orderModal, setOrderModal] = useState(false);

  useEffect(() => {
    fetchCommercial();
  }, []);

  const fetchCommercial = async () => {
    setLoading(true);

    try {
      const result = await NsdlPanCardService.fetchCommercial();

      if (!result.success) {
        Swal.fire(
          "Oops!",
          result.message || "Failed to fetch commercial data",
          "error",
        );
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        setCommercialData(responseData.data);
      } else {
        Swal.fire(
          "Oops!",
          responseData.message || "Failed to fetch commercial data",
          "error",
        );
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      // Always stop loading
      setLoading(false);
    }
  };

  const validateAndProceed = () => {
    if (
      !name ||
      !mobile ||
      !email ||
      !gender ||
      !appMode ||
      !appType ||
      !category ||
      !panType
    ) {
      Swal.fire("Oops!", "Please fill all required fields", "error");
      return;
    }
    if (!/^\d{10}$/.test(mobile)) {
      Swal.fire("Oops!", "Invalid Mobile Number", "error");
      return;
    }
    if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      Swal.fire("Oops!", "Invalid Email Address", "error");
      return;
    }
    if (!checkbox) {
      Swal.fire("Oops!", "Please accept Aadhaar Consent", "error");
      return;
    }

    setOrderModal(true);
  };

  const submitTransaction = async () => {
    // Prevent multiple submissions
    if (loading) return;

    setLoading(true);
    setOrderModal(false);

    try {
      const transactionType = appMode === "E-KYC" ? "EKYC" : "ESIGN";

      const panOption = panType === "e-PAN Card" ? "N" : "Y";

      const result = await NsdlPanCardService.getAuthorization(
        transactionType,
        appType,
        category,
        name,
        "", // DOB removed
        gender,
        mobile,
        email,
        panOption,
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Transaction failed", "error");
        return;
      }

      const responseData = result.data;

      if (responseData.status === "SUCCESS") {
        const data = responseData.data;
        // Open payment/authorization URL in a new tab
        window.open(data.redirect_url, "_blank");
        Swal.fire("Success!", "Transaction initiated successfully!", "success");
      } else {
        Swal.fire(
          "Oops!",
          responseData.message || "Something went wrong",
          "error",
        );
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      // Always stop loading
      setLoading(false);
    }
  };

  const showAadhaarConsent = () => {
    Swal.fire({
      title: "Aadhaar Consent",
      text: "I have no objection in authenticating myself and fully understand that information provided by me shall be used for authenticating my identity through Aadhaar Authentication System for the stated purpose.",
      icon: "info",
    });
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        {/* Sidebar */}
        <SideBar />

        {/* Main Content */}
        <Col md={9}>
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <h4 className="mb-4 text-center">NSDL PAN Card Application</h4>
              <Form>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Application Mode</Form.Label>

                      <Form.Select
                        value={appMode}
                        onChange={(e) => setAppMode(e.target.value)}
                      >
                        <option value="E-KYC">E-KYC</option>
                        <option value="E-SIGN">E-SIGN (SCAN BASED)</option>
                      </Form.Select>

                      {/* Explanation Section */}
                      <div className="mt-2 p-2 bg-light rounded border">
                        {appMode === "E-KYC" ? (
                          <small className="text-success">
                            {" "}
                            {/* RED TEXT */}
                            <strong>E-KYC (Instant PAN):</strong> PAN No gets
                            generated in 30 mins to 2 hours. No need to upload
                            photo or signature. Photo is auto-captured from
                            Aadhaar. Sign after PAN reaches home. 100%
                            paperless.
                          </small>
                        ) : (
                          <small className="text-success">
                            {" "}
                            {/* RED TEXT */}
                            <strong>E-SIGN (Scan Based):</strong> PAN No takes
                            3–5 days. Photo & signature upload required. No
                            physical copy required. 100% paperless.
                          </small>
                        )}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Application Type</Form.Label>
                      <Form.Select
                        value={appType}
                        onChange={(e) => setAppType(e.target.value)}
                      >
                        {!applicationType && (
                          <option value="">Select Application Type</option>
                        )}
                        <option value="49A">
                          PAN - Indian Citizen (Form 49A)
                        </option>
                        <option value="CR">
                          Changes / Correction / Reprint
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Category</Form.Label>
                      <Form.Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        <option value="P">Individual</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Gender</Form.Label>
                      <Form.Select
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                      >
                        <option value="">Select Gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                        <option value="T">Transgender</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Name (As in Aadhaar Card)</Form.Label>
                      <Form.Control
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Mobile Number</Form.Label>
                      <Form.Control
                        type="text"
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                      />
                      <small className="text-mute d-block">
                        PAN number confirmation and courier tracking SMS will be
                        sent to this mobile number.
                      </small>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />

                      <small className="text-mute d-block">
                        e-PAN will be sent to this email directly by Protean.
                      </small>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>PAN Type</Form.Label>
                      <Form.Select
                        value={panType}
                        onChange={(e) => setPanType(e.target.value)}
                      >
                        <option value="Physical PAN Card / e-PAN Card">
                          Physical / e-PAN Card
                        </option>
                        <option value="e-PAN Card">e-PAN Card</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={
                      <span>
                        I accept the{" "}
                        <strong
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                          onClick={showAadhaarConsent}
                        >
                          Aadhaar Consent
                        </strong>
                      </span>
                    }
                    checked={checkbox}
                    onChange={(e) => setCheckbox(e.target.checked)}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  className="w-100 mt-3"
                  onClick={validateAndProceed}
                >
                  Submit
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Confirmation Modal */}
      <Modal show={orderModal} onHide={() => setOrderModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Order Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Order Amount:</strong> ₹
            {panType === "e-PAN Card"
              ? commercialData?.e_pan_fees
              : commercialData?.p_pan_fees}
          </p>
          <p>
            <strong>Commission Amount:</strong> ₹
            {panType === "e-PAN Card"
              ? commercialData?.e_pan_commercial_amount
              : commercialData?.p_pan_commercial_amount}
          </p>
          <p style={{ fontSize: "13px", color: "gray" }}>
            I hereby declare that the above details are correct & verified.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setOrderModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={submitTransaction}>
            Proceed to Pay
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PANApplication;
